import App from 'components/app';
import { appVersion, appVersionHash, isDebug, isDevNodeEnv, sentryDSN, stage } from 'constants/config';
import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import {
  addRequestUrlCustomTag,
  addUserSnapshotContextToEvent,
  consoleCaptureIntegration,
  scrubEvent,
  updateFingerprint
} from 'utils/sentry.utils';
import throttle from 'lodash/throttle';
import { setLogger as setRetryLogger } from '@env0/common-retry';
import { setUseWhatChange } from '@simbathesailor/use-what-changed';
import { startMocks } from 'utils/msw';
import { syncDocumentThemeFromStorage } from 'components/theme-provider-wrapper';

// ORDER IS IMPORTANT
import 'antd/es/style/reset.css';
import '@env0/ui-theme/globals.css';

Object.assign(window, { env0FrontendVersion: appVersion, env0FrontendVersionHash: appVersionHash });

// Loading New Relic and Appcues
declare global {
  interface Window {
    NREUM: any;
    Appcues: any;
    nonce: string;
    cspScriptStarted?: boolean;
    cspScriptFinished?: boolean;
    cspScriptInitialized?: boolean;
  }
}

const UPLOAD_SCREENSHOT_THROTTLE = 5 * 60 * 1000; // 5 minutes
const throttledAddUserSnapshotContextToEvent = throttle(addUserSnapshotContextToEvent, UPLOAD_SCREENSHOT_THROTTLE);

if (sentryDSN) {
  Sentry.init({
    dsn: sentryDSN,
    environment: stage,
    release: `env0@${appVersion}@${appVersionHash}`,
    integrations: [
      consoleCaptureIntegration(),
      Sentry.contextLinesIntegration(),
      Sentry.extraErrorDataIntegration(),
      Sentry.httpClientIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.browserTracingIntegration()
    ],
    // v11 dropped sendDefaultPii; its dataCollection defaults equal what sendDefaultPii: true sent in v10.
    // Keep v10's transactions (dashboards and alerts read them) and stack-trace-free console events.
    traceLifecycle: 'static',
    attachStacktrace: false,
    normalizeDepth: 5,
    beforeSend: async function (event, hint) {
      addRequestUrlCustomTag(event, hint);

      // NOTE: avoid console.log here, as long as CaptureConsole is enabled, it will cause infinite loop
      updateFingerprint(event, hint);
      if (event.level === 'error' || event.level === 'fatal') {
        await throttledAddUserSnapshotContextToEvent(event);
      }

      return scrubEvent(event);
    },
    // a transaction carries breadcrumbs, the page url and request spans too, and beforeSendSpan cannot reach the
    // first two. Deprecated only because it needs traceLifecycle: 'static', set above
    beforeSendTransaction: scrubEvent,
    tracesSampleRate: 0.5,
    profileSessionSampleRate: 0.3,
    profileLifecycle: 'trace',
    ignoreErrors: ["Failed to read the 'cssRules' property from 'CSSStyleSheet'"]
  });
}

// global logger to retry function so whoever uses it will have logs
setRetryLogger({ info: console.info, warning: console.warn, debug: console.debug });

async function main() {
  const isCypress = !!(window as { Cypress?: any }).Cypress;
  if (isDebug && !isCypress) {
    setUseWhatChange(isDevNodeEnv);
    const whyDidYouRender = await import('@welldone-software/why-did-you-render');
    whyDidYouRender.default(React);
  }

  await startMocks();

  // Before the first paint, so the CSS variables match the theme emotion is about to render with.
  syncDocumentThemeFromStorage();

  createRoot(document.getElementById('root') as HTMLElement).render(<App />);
}

main().catch(console.error);
